import { filter, find, isEmpty, noop } from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import PropTypes from 'prop-types';
import { Container, Menu } from 'semantic-ui-react';
import * as Styles from 'src/styles/series-carousel.module.scss';
import { CardCarousel, SeriesCard } from 'src/components/shared';
import { Spinner } from 'react-activity';
import { ContentSelectors, IState } from '@pvolve/sdk/src/redux/selectors';
import { ISeries } from '@pvolve/sdk/src/models/contentful_types';

const connector = connect((state: IState) => ({
    seriesCategories: ContentSelectors.seriesCategory.list(state),
    seriesIndex: ContentSelectors.series.list(state),
}));

interface AllSeriesCategory {
    name: string;
    slug: string;
    series: any;
}

interface SeriesCarouselProps {
    allSeriesCategory: AllSeriesCategory;
    defaultSlug: string;
    showMenu: boolean;
    onboarding: boolean;
    foundationSeriesCategory?: any;
}

const SeriesCarousel = ({
    seriesIndex,
    allSeriesCategory,
    defaultSlug,
    showMenu,
    seriesCategories,
    onboarding,
    foundationSeriesCategory,
}: SeriesCarouselProps & ConnectedProps<typeof connector>) => {
    const extractSeriesCat = ({ fields }) => {
        const catMatch = new RegExp(defaultSlug, 'gi');
        return fields.slug.match(catMatch);
    };
    const isFoundationSeries =
        foundationSeriesCategory || filter(seriesCategories, extractSeriesCat)[0]?.fields;
    const activeSeriesInitialState = defaultSlug ? isFoundationSeries : allSeriesCategory;

    const displayedSeriesInitialState = defaultSlug
        ? isFoundationSeries?.series
        : allSeriesCategory?.series;
    const [activeSeriesCategory, setActiveSeriesCategory] = useState(activeSeriesInitialState);
    const [displayedSeries, setDisplayedSeries] = useState(displayedSeriesInitialState);
    const [hideCarousel, setHideCarousel] = useState(false);

    useEffect(() => {
        setDisplayedSeries(displayedSeriesInitialState);
        setActiveSeriesCategory(activeSeriesInitialState);
    }, [displayedSeriesInitialState, activeSeriesInitialState]);

    const onSeriesCategoryClick = useCallback(
        (slug: string) => {
            // Hide carousel when changing tabs so it can re-render and start from the beginning
            setHideCarousel(true);

            const newSeriesCategory =
                slug === 'all-series'
                    ? allSeriesCategory
                    : find(seriesCategories, { fields: { slug } })?.fields;

            if (newSeriesCategory && newSeriesCategory !== activeSeriesCategory) {
                setActiveSeriesCategory(newSeriesCategory);

                if (slug === 'all-series') {
                    setTimeout(() => {
                        setDisplayedSeries(allSeriesCategory?.series);
                        setHideCarousel(false);
                    });
                } else {
                    const seriesIndexIds = seriesIndex.map((s) => s.sys.id);
                    const publishedSeries = newSeriesCategory.series.filter((s: ISeries) =>
                        seriesIndexIds.includes(s.sys.id)
                    );
                    setTimeout(() => {
                        setDisplayedSeries(publishedSeries);
                        setHideCarousel(false);
                    });
                }
            }
        },
        [activeSeriesCategory, allSeriesCategory, seriesCategories, seriesIndex]
    );

    const RenderItem = ({ data }, index: number) => {
        const datum = data?.node || data;
        return <SeriesCard key={`series-card-${index}`} series={datum} />;
    };

    const SeriesCategoryMenuItem = useCallback(
        ({ fields: { name, order, slug } }, index) =>
            activeSeriesCategory ? (
                <Menu.Item
                    key={`seriesMenuIten-${index}`}
                    name={name}
                    active={activeSeriesCategory?.slug === slug}
                    onClick={onSeriesCategoryClick.bind(this, slug)}
                    style={{ order }}
                />
            ) : null,
        [activeSeriesCategory, onSeriesCategoryClick]
    );

    if (isEmpty(seriesCategories)) {
        return (
            <Container textAlign="center">
                <Spinner size={40} speed={0.75} />
            </Container>
        );
    }

    return (
        <div className={onboarding ? null : Styles.container}>
            {showMenu && (
                <Menu pointing secondary>
                    <Menu.Item
                        name="All Series"
                        active={activeSeriesCategory?.slug === 'all-series'}
                        onClick={onSeriesCategoryClick.bind(this, 'all-series')}
                    />
                    {seriesCategories.map(SeriesCategoryMenuItem)}
                </Menu>
            )}
            {isEmpty(displayedSeries) && <Spinner size={40} speed={0.75} />}
            {!hideCarousel && !isEmpty(displayedSeries) && (
                <CardCarousel
                    naturalSlideHeight={610}
                    naturalSlideWidth={430}
                    title={activeSeriesCategory?.name}
                    to={`/series/categories/${activeSeriesCategory?.slug}`}
                    data={displayedSeries}
                    renderItem={RenderItem}
                />
            )}
        </div>
    );
};

SeriesCarousel.propTypes = {
    series: PropTypes.object,
    onVideoClick: PropTypes.func,
};

SeriesCarousel.defaultProps = {
    onVideoClick: () => noop,
};

export default connector(SeriesCarousel);
